import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";

import { Icon } from "../components/Icon";
import {
  Empty,
  Pill,
  ToastProvider,
  errText,
  relativeTime,
  useToast,
} from "../admin/ui";
import {
  api,
  type AdminCompanyRow,
  type KbAdminListItem,
  type KbAdminPage,
} from "../services/api";

type Tab = "all" | "published" | "draft";

const PAGE_SIZE = 25;

/**
 * Knowledge base list.
 *
 * Articles for every customer, or just one. Publishing is one step (with
 * versioned rollback in the editor), so the list carries an inline Publish for
 * anything still in draft.
 */
export function AdminKB() {
  return (
    <ToastProvider>
      <AdminKBInner />
    </ToastProvider>
  );
}

function AdminKBInner() {
  const navigate = useNavigate();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>("all");
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data, isLoading, mutate } = useSWR<KbAdminPage>(
    ["/api/admin/kb", tab, page],
    () =>
      api.adminKb.list({
        status: tab === "all" ? undefined : tab,
        page,
        page_size: PAGE_SIZE,
      }),
    { keepPreviousData: true },
  );
  const { data: companies } = useSWR<AdminCompanyRow[]>("/api/admin/companies", () =>
    api.admin.companies.list(),
  );

  const nameOf = useMemo(() => {
    const byId = new Map((companies ?? []).map((c) => [c.id, c.name]));
    return (id: string) => byId.get(id) ?? "Unknown customer";
  }, [companies]);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function switchTab(next: Tab) {
    setTab(next);
    setPage(1);
  }

  async function publish(article: KbAdminListItem) {
    setBusyId(article.id);
    try {
      await api.adminKb.publish(article.id);
      await mutate();
      toast(`Published ${article.title}`);
    } catch (e) {
      toast(errText(e, "Could not publish that article"), "warn");
    } finally {
      setBusyId(null);
    }
  }

  async function remove(article: KbAdminListItem) {
    if (!window.confirm(`Delete “${article.title}”? This cannot be undone.`)) return;
    setBusyId(article.id);
    try {
      await api.adminKb.remove(article.id);
      await mutate();
      toast(`${article.title} deleted`);
    } catch (e) {
      toast(errText(e, "Could not delete that article"), "warn");
    } finally {
      setBusyId(null);
    }
  }

  function audience(a: KbAdminListItem) {
    if (a.scope === "global") return <span className="ad-scope all">All customers</span>;
    if (a.scope === "single_company") {
      return <span className="ad-scope">{a.company_id ? nameOf(a.company_id) : "One customer"}</span>;
    }
    return <span className="ad-scope">Selected customers</span>;
  }

  return (
    <div className="rf-wrap">
      <div className="ad-head">
        <div>
          <div className="ad-eyebrow">Admin</div>
          <h1 className="rf-h1">Knowledge base</h1>
          <p className="rf-sub">
            Articles for every customer, or just one. Drafts stay private until you
            publish.
          </p>
        </div>
        <div className="grp">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate("/admin/kb/new")}
          >
            <Icon name="plus" size={15} /> New article
          </button>
        </div>
      </div>

      <div className="ad-tabs">
        {(
          [
            ["all", "All"],
            ["published", "Published"],
            ["draft", "Drafts"],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`ad-tab${tab === key ? " active" : ""}`}
            onClick={() => switchTab(key)}
            onFocus={(e) => e.currentTarget.scrollIntoView({ block: "nearest", inline: "nearest" })}
          >
            {label}
            {tab === key && <span className="c">{total}</span>}
          </button>
        ))}
      </div>

      <div className="ad-card">
        {isLoading && !data ? (
          <div style={{ padding: 18 }}>Loading articles…</div>
        ) : items.length === 0 ? (
          <Empty
            title="No articles here"
            blurb={
              tab === "all"
                ? "Write the first article to get started."
                : `No ${tab} articles yet.`
            }
          />
        ) : (
          <div className="ad-tablewrap">
            <table className="ad-table">
              <thead>
                <tr>
                  <th>Article</th>
                  <th>Audience</th>
                  <th>Updated</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr
                    key={a.id}
                    className="clickable"
                    onClick={() => navigate(`/admin/kb/${a.id}/edit`)}
                  >
                    <td>
                      <span className="who">
                        <span className="ad-av tile green">
                          <Icon name="book" />
                        </span>
                        <span>
                          <span className="n">{a.title}</span>
                          <br />
                          <span className="e">{a.category}</span>
                        </span>
                      </span>
                    </td>
                    <td>{audience(a)}</td>
                    <td className="mono">{relativeTime(a.updated_at) ?? "—"}</td>
                    <td>
                      {a.status === "published" ? (
                        <Pill kind="ok">Published</Pill>
                      ) : (
                        <Pill kind="warn">Draft</Pill>
                      )}
                    </td>
                    <td className="r" onClick={(e) => e.stopPropagation()}>
                      {a.status !== "published" && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={busyId === a.id}
                          onClick={() => void publish(a)}
                        >
                          {busyId === a.id ? "Publishing…" : "Publish"}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        disabled={busyId === a.id}
                        onClick={() => void remove(a)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              padding: "12px 16px",
              borderTop: "1px solid var(--iq-border)",
            }}
          >
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Previous
            </button>
            <span style={{ fontSize: 13, color: "var(--iq-text-muted)" }}>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
